import React, { useEffect, useState, useCallback } from 'react';
import { FormattedMessage } from 'react-intl';
import {
    Button,
    Card,
    Flex,
    Heading,
    Image,
    Text,
    View,
    Link,
    Divider,
    Loader,
    useAuthenticator,
    Alert
} from '@aws-amplify/ui-react';
import { fetchUserAttributes } from 'aws-amplify/auth';
import { useCustomSignOut } from '../hooks/useCustomSignOut';
import { isSameDomain } from '../utils/auth';
import { EmailChangeForm } from './EmailChangeForm';
import { PasswordChangeForm } from './PasswordChangeForm';
import { LanguageSwitcher } from './LanguageSwitcher';
import { fetchWithRetry, FetchError } from '../utils/fetchWithRetry';
import { logError } from '../utils/errorLogger';
import { ErrorDisplay } from './ErrorDisplay';

// メールアドレス変更機能の有効/無効フラグ
// この変数を true に変更するだけでメールアドレス変更機能が復活します
const EMAIL_CHANGE_ENABLED = false;

interface UserInfo {
    username: string;
    userId: string;
    email: string;
    sub: string;
    idTokenPayload: any;
    identities?: string;
}

export const AuthenticatedHome: React.FC = () => {
    const { user, authStatus } = useAuthenticator((context) => [context.user, context.authStatus]);
    const customSignOut = useCustomSignOut();
    const [userInfo, setUserInfo] = useState<UserInfo | null>(null);
    const [loading, setLoading] = useState(true);
    const [redirectUri, setRedirectUri] = useState<string | null>(null);
    const [showEmailChangeForm, setShowEmailChangeForm] = useState(false);
    const [showPasswordChangeForm, setShowPasswordChangeForm] = useState(false);
    const [error, setError] = useState<FetchError | null>(null);
    const [isRetrying, setIsRetrying] = useState(false);

    // Googleユーザーかどうかを判定する関数
    const isGoogleUser = useCallback((user: UserInfo | null): boolean => {
        if (!user || !user.identities) return false;

        try {
            const identities = JSON.parse(user.identities);
            return identities.some((identity: any) =>
                identity.providerType === 'Google' ||
                identity.providerName === 'Google'
            );
        } catch (e) {
            console.error('Error parsing identities:', e);
            return false;
        }
    }, []);

    // Amplifyが自動的にクッキーを管理するため、手動更新は不要

    const refreshUserInfo = useCallback(async () => {
        if (authStatus === 'authenticated' && user) {
            try {
                setLoading(true);
                setError(null);
                setIsRetrying(false);

                console.log('🔄 Fetching user attributes with retry mechanism...');

                // タイムアウト・リトライ機能付きでユーザー属性を取得
                const attributes = await fetchWithRetry(
                    () => fetchUserAttributes(),
                    {
                        maxRetries: 3,
                        timeoutMs: 10000, // 10秒
                        initialDelayMs: 1000,
                        maxDelayMs: 4000
                    }
                );

                console.log('✅ User attributes fetched successfully');

                // subはuserIdと同じ値（Cognito User Pool のユーザー識別子）
                setUserInfo({
                    username: user.username || '',
                    userId: user.userId || '',
                    email: attributes.email || '',
                    sub: attributes.sub || user.userId || '',
                    idTokenPayload: {},
                    identities: attributes.identities
                });

            } catch (err: any) {
                console.error('❌ Failed to fetch user info after retries:', err);
                
                // エラーをログに記録
                const fetchError = err as FetchError;
                logError(fetchError, 'refreshUserInfo');
                
                // 認証エラーの場合は、エラーメッセージを表示せず静かにログアウト
                if (!fetchError.isRetryable && authStatus === 'authenticated') {
                    console.log('🚪 Session invalid. Signing out silently...');
                    setLoading(false);
                    // エラーステートは設定しない（エラーメッセージを表示しない）
                    customSignOut();
                } else {
                    // リトライ可能なエラー（ネットワークエラーなど）の場合のみエラー表示
                    setError(fetchError);
                }
            } finally {
                setLoading(false);
            }
        }
    }, [authStatus, user, customSignOut]);

    useEffect(() => {
        const initializeUserInfo = async () => {
            // 認証済みの場合のみユーザー情報を取得
            if (authStatus === 'authenticated' && user) {
                await refreshUserInfo();

                const storedRedirectUri = localStorage.getItem('redirect_uri');
                if (storedRedirectUri && isSameDomain(storedRedirectUri)) {
                    setRedirectUri(storedRedirectUri);
                }
            }
        };

        initializeUserInfo();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [user, authStatus]);

    const handleToggleEmailChangeForm = useCallback(() => {
        setShowEmailChangeForm(!showEmailChangeForm);
        if (showPasswordChangeForm) setShowPasswordChangeForm(false);
    }, [showEmailChangeForm, showPasswordChangeForm]);

    const handleTogglePasswordChangeForm = useCallback(() => {
        setShowPasswordChangeForm(!showPasswordChangeForm);
        if (showEmailChangeForm) setShowEmailChangeForm(false);
    }, [showPasswordChangeForm, showEmailChangeForm]);

    const handleEmailChangeSuccess = useCallback(async () => {
        await refreshUserInfo();
        setShowEmailChangeForm(false);
    }, [refreshUserInfo]);

    const handlePasswordChangeSuccess = useCallback(() => {
        setShowPasswordChangeForm(false);
    }, []);

    // リトライハンドラー
    const handleRetry = useCallback(() => {
        console.log('🔄 Manual retry triggered');
        setIsRetrying(true);
        refreshUserInfo();
    }, [refreshUserInfo]);

    const handleSignOut = async () => {
        try {
            // サインアウト前にエラーステートをクリア
            setError(null);
            setLoading(false);
            
            const result = await customSignOut();
            localStorage.removeItem('redirect_uri');
            return result;
        } catch (error) {
            console.error('Error during sign out:', error);
            return { success: false, error };
        }
    };

    const handleRedirect = () => {
        if (redirectUri) {
            window.location.href = redirectUri;
            localStorage.removeItem('redirect_uri');
        }
    };

    // エラー表示
    if (error && !loading) {
        return (
            <ErrorDisplay
                error={error}
                onRetry={error.isRetryable ? handleRetry : undefined}
                onSignOut={!error.isRetryable ? customSignOut : undefined}
                showDetails={true}
            />
        );
    }

    // ローディング表示
    if (loading || !userInfo) {
        return (
            <View padding="1rem">
                <Flex justifyContent="center" alignItems="center" height="100vh" direction="column" gap="1rem">
                    <Loader size="large" />
                    {isRetrying && (
                        <Text>再試行中...</Text>
                    )}
                </Flex>
            </View>
        );
    }

    return (
        <View
            padding="1rem"
            maxWidth="480px"
            margin="0 auto"
        >
            <Card>
                <Flex direction="column" alignItems="center">
                    <Link
                        href="https://www.yolo-japan.com?utm_source=yj&utm_medium=logo&utm_campaign=yj_logo_cognito_yj"
                        isExternal={false}
                        style={{ textDecoration: 'none' }}
                    >
                        <Image
                            alt="YOLO JAPAN logo"
                            src="./cognito-logo.webp"
                            srcSet="./cognito-logo.webp 1x, ./cognito-logo_2x.webp 2x"
                            width="100%"
                            marginBottom="1rem"
                        />
                    </Link>
                    <Heading level={3} fontSize="1.5rem" textAlign="center">
                        <FormattedMessage id="welcomeMessage" defaultMessage="Welcome to YOLO JAPAN" />
                    </Heading>

                    {/* 言語切替コンポーネント */}
                    <View marginTop="1rem">
                        <LanguageSwitcher />
                    </View>
                </Flex>

                <Divider marginTop="1.5rem" marginBottom="1.5rem" />

                {userInfo && (
                    <Flex direction="column" gap="1rem">
                        <Heading level={5} fontSize="1.1rem">
                            <FormattedMessage id="userInfoHeading" defaultMessage="User Information" />
                        </Heading>

                        <Flex direction="column" gap="0.5rem">
                            <Text fontSize="0.9rem" style={{ wordBreak: 'break-all' }}>
                                <FormattedMessage id="emailLabel" defaultMessage="Email" />: {userInfo.email}
                            </Text>

                            {isGoogleUser(userInfo) && EMAIL_CHANGE_ENABLED ? (
                                <Alert variation="info" hasIcon={true} fontSize="0.8rem" padding="0.5rem">
                                    <FormattedMessage
                                        id="googleEmailChangeInfo"
                                        defaultMessage="You are signed in with Google. To change your email address, please update it in your Google account settings."
                                    />
                                </Alert>
                            ) : EMAIL_CHANGE_ENABLED && (
                                <Button
                                    onClick={handleToggleEmailChangeForm}
                                    variation="link"
                                    size="small"
                                    padding="0"
                                    fontSize="0.9rem"
                                >
                                    <FormattedMessage
                                        id={showEmailChangeForm ? "hideEmailChangeForm" : "showEmailChangeForm"}
                                        defaultMessage={showEmailChangeForm ? "Cancel" : "Change Email"}
                                    />
                                </Button>
                            )}
                        </Flex>

                        {showEmailChangeForm && userInfo.email && !isGoogleUser(userInfo) && EMAIL_CHANGE_ENABLED && (
                            <EmailChangeForm
                                currentEmail={userInfo.email}
                                onSuccess={handleEmailChangeSuccess}
                            />
                        )}

                        {/* パスワード変更セクション */}
                        {isGoogleUser(userInfo) ? (
                            <Alert variation="info" hasIcon={true} fontSize="0.8rem" padding="0.5rem">
                                <FormattedMessage
                                    id="googlePasswordChangeInfo"
                                    defaultMessage="You are signed in with Google. To change your password, please update it in your Google account settings."
                                />
                            </Alert>
                        ) : (
                            <Flex direction="column" gap="0.5rem" marginTop="0.5rem">
                                <Button
                                    onClick={handleTogglePasswordChangeForm}
                                    variation="link"
                                    size="small"
                                    padding="0"
                                    fontSize="0.9rem"
                                >
                                    <FormattedMessage
                                        id={showPasswordChangeForm ? "hidePasswordChangeForm" : "showPasswordChangeForm"}
                                        defaultMessage={showPasswordChangeForm ? "Cancel" : "Change Password"}
                                    />
                                </Button>

                                {showPasswordChangeForm && (
                                    <PasswordChangeForm onSuccess={handlePasswordChangeSuccess} />
                                )}
                            </Flex>
                        )}

                        <Text
                            variation="tertiary"
                            fontSize="0.75rem"
                            marginTop="0.5rem"
                            style={{ wordBreak: 'break-all' }}
                        >
                            <FormattedMessage id="userIdLabel" defaultMessage="User ID" />: {userInfo.userId}
                        </Text>
                    </Flex>
                )}

                <Flex justifyContent="center" marginTop="2rem" gap="1rem" direction={{ base: 'column', medium: 'row' }}>
                    {redirectUri && (
                        <Button onClick={handleRedirect} variation="primary" width="100%">
                            <FormattedMessage id="returnToAppButton" defaultMessage="Return to Site" />
                        </Button>
                    )}
                    <Button onClick={handleSignOut} width="100%">
                        <FormattedMessage id="signOutButton" defaultMessage="Sign Out" />
                    </Button>
                </Flex>
            </Card>
        </View>
    );
};